import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { SOURCE_WINDOW_MAX_COLS, SOURCE_WINDOW_MAX_ROWS } from '@services/fmdc';
import { getColumnLabel } from '@features/tableBuilder/tableUtils';
import { formatCell } from './formatCell';
import { LinkButton, SectionHead } from './styles';

// A read-only window of the model's cells, for showing a value in context. The tableBuilder Table
// is not reused: it mounts an editing toolbar, opens insert/delete menus on header click, has no
// row labels or offsets and keeps its active cell private. This is thin: a styled table in the
// same look (lowContrastBorder cell borders, a sticky header row and a sticky label column) built
// from the window the server already laid out.

const Scroll = styled.div`
  max-height: 480px;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
`;

const Grid = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  color: ${({ theme }) => theme.themeColors.text};

  th,
  td {
    border-right: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
    border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
    padding: 4px 10px;
    text-align: right;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
    height: 24px;
    box-sizing: border-box;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
    color: ${({ theme }) => theme.themeColors.secondaryText};
    font-weight: inherit;
    font-family: inherit;
  }

  th.label,
  td.label {
    position: sticky;
    left: 0;
    z-index: 1;
    text-align: left;
    min-width: 160px;
    max-width: 260px;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
    font-family: inherit;
  }

  th.label {
    z-index: 3;
  }

  tbody tr:hover td {
    background: ${({ theme }) => theme.themeColors.hoverComponentBackground};
  }
`;

const Cell = styled.td`
  color: ${({ theme, $isError }) => ($isError ? theme.themeColors.error : theme.themeColors.text)};
  font-style: ${({ $isError }) => ($isError ? 'italic' : 'normal')};
  font-weight: ${({ $isFocused }) => ($isFocused ? 700 : 400)};
  box-shadow: ${({ theme, $isFocused }) =>
    $isFocused ? `inset 0 0 0 2px ${theme.themeColors.primaryCtaButton}` : 'none'};
`;

const RowNumber = styled.span`
  color: ${({ theme }) => theme.themeColors.secondaryText};
  margin-right: 8px;
`;

const range = (from, to) => {
  const out = [];
  for (let i = from; i <= to; i += 1) out.push(i);
  return out;
};

// 'Financials!K13' and 'K13' name the same cell.
const bareCoordinate = (coordinate) => {
  if (!coordinate) return '';
  const bang = String(coordinate).lastIndexOf('!');
  return bang >= 0 ? String(coordinate).slice(bang + 1) : String(coordinate);
};

export const isFocusedCell = (cell, focusCell) => {
  if (!cell || !focusCell) return false;
  if (typeof focusCell === 'object') return cell.row === focusCell.row && cell.col === focusCell.col;
  return bareCoordinate(cell.coordinate).toUpperCase() === bareCoordinate(focusCell).toUpperCase();
};

// The next window out from this one: five more rows and columns each way, clamped to the
// server's caps and to the sheet's first row and column. Returns null when there is no room left.
export const widenWindow = (window, { step = 5 } = {}) => {
  if (!window) return null;
  const rowFrom = Number(window.row_from);
  const rowTo = Number(window.row_to);
  const colFrom = Number(window.col_from);
  const colTo = Number(window.col_to);
  const rows = rowTo - rowFrom + 1;
  const cols = colTo - colFrom + 1;
  const extraRows = Math.max(0, Math.min(step, Math.floor((SOURCE_WINDOW_MAX_ROWS - rows) / 2)));
  const extraCols = Math.max(0, Math.min(step, Math.floor((SOURCE_WINDOW_MAX_COLS - cols) / 2)));
  const next = {
    sheet: window.sheet,
    rowFrom: Math.max(1, rowFrom - extraRows),
    rowTo: rowTo + extraRows,
    colFrom: Math.max(1, colFrom - extraCols),
    colTo: colTo + extraCols,
  };
  if (next.rowFrom === rowFrom && next.rowTo === rowTo && next.colFrom === colFrom && next.colTo === colTo) return null;
  return next;
};

// The column header for each column: the first non-empty `column_header` any cell in that column
// carries, else the Excel letter (getColumnLabel is 0-based; sheet columns are 1-based).
export const columnHeaders = (window) => {
  const headers = new Map();
  (window?.cells || []).forEach((cell) => {
    if (cell.column_header && !headers.has(cell.col)) headers.set(cell.col, String(cell.column_header));
  });
  return (col) => headers.get(col) || getColumnLabel(col - 1);
};

export const SourceGrid = ({ window, focusCell, onWiden, isLoading = false, error = null, onRetry }) => {
  const focusedRef = React.useRef(null);
  const focusKey = typeof focusCell === 'object' && focusCell ? `${focusCell.row}:${focusCell.col}` : focusCell;

  // Bring the value's cell into view once the window has rendered; jsdom has no scrollIntoView.
  React.useEffect(() => {
    const node = focusedRef.current;
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [focusKey, window]);

  const cellMap = React.useMemo(() => {
    const map = new Map();
    (window?.cells || []).forEach((cell) => map.set(`${cell.row}:${cell.col}`, cell));
    return map;
  }, [window]);

  const headerFor = React.useMemo(() => columnHeaders(window), [window]);
  const canWiden = !!onWiden && !!widenWindow(window);

  return (
    <div>
      <Container row spaceBetween verticallyCenter>
        <SectionHead
          heading="Source cells"
          subtext={window ? `${window.sheet}, rows ${window.row_from}–${window.row_to}` : 'The model around this value'}
        />
        {canWiden && <LinkButton onClick={onWiden}>Wider window</LinkButton>}
      </Container>
      <Container top={12}>
        {isLoading && !window && <Body5 isLowContrast>Loading source cells…</Body5>}
        {error && !window && (
          <div>
            <Body5>{error}</Body5>
            {onRetry && (
              <Container top={12}>
                <SkeletonButton onClick={onRetry}>Retry</SkeletonButton>
              </Container>
            )}
          </div>
        )}
        {window && (
          <Scroll>
            <Grid aria-label={`Source cells of ${window.sheet}`}>
              <thead>
                <tr>
                  <th className="label" scope="col">
                    Row
                  </th>
                  {range(Number(window.col_from), Number(window.col_to)).map((col) => (
                    <th key={col} scope="col" title={getColumnLabel(col - 1)}>
                      {headerFor(col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {range(Number(window.row_from), Number(window.row_to)).map((row) => (
                  <tr key={row}>
                    <td className="label" title={window.row_labels?.[row] || ''}>
                      <RowNumber>{row}</RowNumber>
                      {window.row_labels?.[row] || ''}
                    </td>
                    {range(Number(window.col_from), Number(window.col_to)).map((col) => {
                      const cell = cellMap.get(`${row}:${col}`);
                      const focused = isFocusedCell(cell, focusCell);
                      const text = cell ? formatCell(cell.value, cell.number_format) : '';
                      return (
                        <Cell
                          key={col}
                          ref={focused ? focusedRef : undefined}
                          $isError={!!cell?.is_error}
                          $isFocused={focused}
                          aria-current={focused ? 'true' : undefined}
                          title={
                            cell
                              ? cell.formula ||
                                (cell.value === null || cell.value === undefined ? '' : String(cell.value))
                              : ''
                          }
                        >
                          {text}
                        </Cell>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Grid>
          </Scroll>
        )}
        {window && isLoading && (
          <Container top={8}>
            <Body7 isLowContrast role="status">
              Loading a wider window…
            </Body7>
          </Container>
        )}
      </Container>
    </div>
  );
};
